import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type DocumentNode,
  gql,
  useMutation,
} from "@apollo/client";
import { Decimal } from "decimal.js";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DRAFT_PAYMENT_FIELDS } from "./get-draft-payments";

type CreateDraftPaymentInput = {
  rawAmount: number | null;
  executionDate: Date | null;
  recipientName: string | null;
  recipientIban: string | null;
  purpose: string | null;
  currency: string | null;
};

export const useCreateDraftPayment = ({
  refetchQueries,
}: {
  refetchQueries?: string[] | DocumentNode[];
} = {}): [
  (invoiceId: string, input: Partial<CreateDraftPaymentInput>) => Promise<any>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [createDraftPayment, { loading, error, data }] = useMutation(
    CREATE_DRAFT_PAYMENT_MUTATION,
    {
      refetchQueries,
    },
  );

  const handleCreateDraftPayment = async (
    invoiceId: string,
    input: Partial<CreateDraftPaymentInput>,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await createDraftPayment({
        variables: {
          tenantId,
          invoiceId,
          ...input,
        },
      });

      toast.dismiss(toastId);

      return response.createDraftPayment;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleCreateDraftPayment, { data, loading, error }];
};

export const CREATE_DRAFT_PAYMENT_MUTATION = gql`
  mutation CreateDraftPayment(
    $tenantId: String!
    $invoiceId: String!
    $rawAmount: Float
    $currency: String
    $executionDate: String
    $recipientName: String
    $recipientIban: String
    $purpose: String
  ) {
    createDraftPayment(
      tenantId: $tenantId
      invoiceId: $invoiceId
      rawAmount: $rawAmount
      currency: $currency
      executionDate: $executionDate
      recipientName: $recipientName
      recipientIban: $recipientIban
      purpose: $purpose
    ) {
      ...DraftPaymentFields
    }
  }
  ${DRAFT_PAYMENT_FIELDS}
`;
